import React, { useEffect } from 'react';
import Layout from '../layouts/Layout';
import { PUBLIC_URL } from '../utils/const';
import { BlogLanguageWrapper, BlogPage } from 'reactyll';
import MarkDown from 'react-markdown'
import rehypeRaw from 'rehype-raw';
import { Head } from 'vite-react-ssg';
import { useFathom } from '../contexts/FathomContext';
import { Container, Title, Text, Image, Box, Paper, Anchor, Group, Button, Stack, Divider } from '@mantine/core';
import { redirectWithParameters } from '../utils/common';
import { getBlogByLanguage } from '../utils/blog';
import { blogs } from '../_blog/routes';
import { useTranslation } from 'react-i18next';
import MoreFromOurBlog from '../components/MoreFromOurBlog';

export interface ExtraProperties {
    date: string
    headline: string
    description: string
    publishDate: string
    readMore?: string[]
}

const content: BlogPage<ExtraProperties> = JSON.parse(decodeURIComponent(atob(`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`)));


const InnerBlogTemplate: React.FC = () => {
    const { trackEvent } = useFathom();
    const { i18n } = useTranslation();

    const matchingLanguageCode = content.properties.language || i18n.resolvedLanguage;    
    
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const langParam = urlParams.get('lang');

        if (langParam) {
            const matchingLanguage = content.languages.find(([lang, url]) => lang === langParam);
            if (matchingLanguage) {
                const [_, url] = matchingLanguage;
                window.location.href = `${url}`;
            }
        }
        

        const handleButtonClick = (event: any) => {
            if (event.target.classList.contains('cta')) {
                console.log("CTA clicked")
                trackEvent("Blog > CTA Clicked");
                window.location.href = "/login?signup=true"
            }
        };

        const parentElement = document.getElementById('blog-wrapper');
        if (parentElement) {
            document.addEventListener('click', handleButtonClick);
        }

        return () => {
            if (parentElement) {
                parentElement.removeEventListener('click', handleButtonClick);
            }
        };
    }, []);

    return (
        <Box bg="gray.0">
            <Head>
                <meta charSet="utf-8" />
                <title>{content.properties.title}</title>
                <link rel="canonical" href={`https://calensync.live${content.properties.url}`} />
                {content.languages.map(([language, url]) => (
                    <link rel="alternate" key={language} href={`https://calensync.live${url}`} hrefLang={language} />
                ))}
                <meta name="description" content={content.properties.description} />
                <meta property="og:title" content={content.properties.title} />
                <meta property="og:url" content={`https://calensync.live${content.properties.url}`} />
                <meta property="og:description" content={content.properties.description} />
                <meta property="og:publish_date" content={content.properties.publishDate} />
            </Head>
            <Container size="md" mt="md" id="blog-wrapper">
                <Paper p="md" my="md" bg="gray.0">
                    <Box mb="md" mt="md">
                        <Title order={1} mb="xs">{content.properties.title}</Title>
                        <Text fs="italic" c="dimmed">{content.properties.date}</Text>
                    </Box>

                    <Box mb="xl">
                        <MarkDown
                            rehypePlugins={[rehypeRaw]}
                            children={content.markdown}
                            components={{
                                h1(props) {
                                    const { children } = props
                                    return <Title order={1}>{children}</Title>
                                },
                                p(props) {
                                    const { children } = props
                                    return <Text size="lg" mb="md">
                                        {children}
                                    </Text>
                                },
                                strong(props) {
                                    const { children } = props
                                    return <Text span fw={700}><b>{children}</b></Text>
                                },
                                h2(props) {
                                    const { children } = props
                                    return <Title fw={400} order={2} mt="xl" pt="md" pb="md">{children}</Title>
                                },
                                h3(props) {
                                    const { children } = props
                                    return <Title fz="xl" fw={300} order={3} mt="md" pt="sm" pb="sm">{children}</Title>
                                },
                                img(props) {
                                    const { src, alt, style, width } = props
                                    return <Image
                                        mx="auto"
                                        my="sm"
                                        src={src}
                                        alt={alt}
                                        style={style}
                                        w={width}
                                        radius="md"
                                    />
                                },
                                a(props) {
                                    const { children, href } = props
                                    if (typeof children === 'string' && children.includes('CUSTOM_BUTTON')) {
                                        const content = children.replace('CUSTOM_BUTTON:', '')
                                        const onClick = () => {
                                            console.log("CTA clicked")
                                            trackEvent("Blog > CTA Clicked");
                                            redirectWithParameters('login', { signup: 'true' }, true)
                                        }
                                        return (
                                            <Group justify='center' my='md'>
                                                <Stack justify='center' my='md' gap="0">
                                                    <Button color="primary.4" size="compact-md" className="cta" onClick={onClick}>{content}</Button>
                                                    <Text size="sm" c="dimmed">.. and get 7 days free trial</Text>
                                                </Stack>
                                            </Group>
                                        )
                                    } else if (href.toLowerCase().startsWith('blog/')) {
                                        const blogId = href.toLowerCase().replace('blog/', '')
                                        const content = getBlogByLanguage(blogs[blogId], matchingLanguageCode)
                                        return <Anchor href={content.url}>{children}</Anchor>
                                    } else {
                                        return <Anchor href={href}>{children}</Anchor>
                                    }
                                }
                            }}
                        />
                    </Box>
                    {content.properties.readMore && (
                        <MoreFromOurBlog 
                            blogs={content.properties.readMore.map((blogId) => blogs[blogId])} 
                            matchingLanguageCode={matchingLanguageCode} 
                        />
                    )}
                </Paper>
            </Container>
        </Box>
    );
};

const BlogTemplate: React.FC = () => {
    return (
        <Layout>
            <InnerBlogTemplate />
        </Layout>
    );
};

export default BlogTemplate;